import React, { useEffect } from 'react';
import Layout from '../layouts/Layout';
import { PUBLIC_URL } from '../utils/const';
import { BlogLanguageWrapper, BlogPage } from 'reactyll';
import MarkDown from 'react-markdown'
import rehypeRaw from 'rehype-raw';
import { Head } from 'vite-react-ssg';
import { useFathom } from '../contexts/FathomContext';
import { Container, Title, Text, Image, Box, Paper, Anchor, Group, Button, Stack, Divider } from '@mantine/core';
import { redirectWithParameters } from '../utils/common';
import { getBlogByLanguage } from '../utils/blog';
import { blogs } from '../_blog/routes';
import { useTranslation } from 'react-i18next';
import MoreFromOurBlog from '../components/MoreFromOurBlog';

export interface ExtraProperties {
    date: string
    headline: string
    description: string
    publishDate: string
    readMore?: string[]
}

const content: BlogPage<ExtraProperties> = JSON.parse(decodeURIComponent(atob(`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`)));


const InnerBlogTemplate: React.FC = () => {
    const { trackEvent } = useFathom();
    const { i18n } = useTranslation();

    const matchingLanguageCode = content.properties.language || i18n.resolvedLanguage;    
    
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const langParam = urlParams.get('lang');

        if (langParam) {
            const matchingLanguage = content.languages.find(([lang, url]) => lang === langParam);
            if (matchingLanguage) {
                const [_, url] = matchingLanguage;
                window.location.href = `${url}`;
            }
        }
        

        const handleButtonClick = (event: any) => {
            if (event.target.classList.contains('cta')) {
                console.log("CTA clicked")
                trackEvent("Blog > CTA Clicked");
                window.location.href = "/login?signup=true"
            }
        };

        const parentElement = document.getElementById('blog-wrapper');
        if (parentElement) {
            document.addEventListener('click', handleButtonClick);
        }

        return () => {
            if (parentElement) {
                parentElement.removeEventListener('click', handleButtonClick);
            }
        };
    }, []);

    return (
        <Box bg="gray.0">
            <Head>
                <meta charSet="utf-8" />
                <title>{content.properties.title}</title>
                <link rel="canonical" href={`https://calensync.live${content.properties.url}`} />
                {content.languages.map(([language, url]) => (
                    <link rel="alternate" key={language} href={`https://calensync.live${url}`} hrefLang={language} />
                ))}
                <meta name="description" content={content.properties.description} />
                <meta property="og:title" content={content.properties.title} />
                <meta property="og:url" content={`https://calensync.live${content.properties.url}`} />
                <meta property="og:description" content={content.properties.description} />
                <meta property="og:publish_date" content={content.properties.publishDate} />
            </Head>
            <Container size="md" mt="md" id="blog-wrapper">
                <Paper p="md" my="md" bg="gray.0">
                    <Box mb="md" mt="md">
                        <Title order={1} mb="xs">{content.properties.title}</Title>
                        <Text fs="italic" c="dimmed">{content.properties.date}</Text>
                    </Box>

                    <Box mb="xl">
                        <MarkDown
                            rehypePlugins={[rehypeRaw]}
                            children={content.markdown}
                            components={{
                                h1(props) {
                                    const { children } = props
                                    return <Title order={1}>{children}</Title>
                                },
                                p(props) {
                                    const { children } = props
                                    return <Text size="lg" mb="md">
                                        {children}
                                    </Text>
                                },
                                strong(props) {
                                    const { children } = props
                                    return <Text span fw={700}><b>{children}</b></Text>
                                },
                                h2(props) {
                                    const { children } = props
                                    return <Title fw={400} order={2} mt="xl" pt="md" pb="md">{children}</Title>
                                },
                                h3(props) {
                                    const { children } = props
                                    return <Title fz="xl" fw={300} order={3} mt="md" pt="sm" pb="sm">{children}</Title>
                                },
                                img(props) {
                                    const { src, alt, style, width } = props
                                    return <Image
                                        mx="auto"
                                        my="sm"
                                        src={src}
                                        alt={alt}
                                        style={style}
                                        w={width}
                                        radius="md"
                                    />
                                },
                                a(props) {
                                    const { children, href } = props
                                    if (typeof children === 'string' && children.includes('CUSTOM_BUTTON')) {
                                        const content = children.replace('CUSTOM_BUTTON:', '')
                                        const onClick = () => {
                                            console.log("CTA clicked")
                                            trackEvent("Blog > CTA Clicked");
                                            redirectWithParameters('login', { signup: 'true' }, true)
                                        }
                                        return (
                                            <Group justify='center' my='md'>
                                                <Stack justify='center' my='md' gap="0">
                                                    <Button color="primary.4" size="compact-md" className="cta" onClick={onClick}>{content}</Button>
                                                    <Text size="sm" c="dimmed">.. and get 7 days free trial</Text>
                                                </Stack>
                                            </Group>
                                        )
                                    } else if (href.toLowerCase().startsWith('blog/')) {
                                        const blogId = href.toLowerCase().replace('blog/', '')
                                        const content = getBlogByLanguage(blogs[blogId], matchingLanguageCode)
                                        return <Anchor href={content.url}>{children}</Anchor>
                                    } else {
                                        return <Anchor href={href}>{children}</Anchor>
                                    }
                                }
                            }}
                        />
                    </Box>
                    {content.properties.readMore && (
                        <MoreFromOurBlog 
                            blogs={content.properties.readMore.map((blogId) => blogs[blogId])} 
                            matchingLanguageCode={matchingLanguageCode} 
                        />
                    )}
                </Paper>
            </Container>
        </Box>
    );
};

const BlogTemplate: React.FC = () => {
    return (
        <Layout>
            <InnerBlogTemplate />
        </Layout>
    );
};

export default BlogTemplate;